import React, { useEffect } from 'react';
import Layout from '../layouts/Layout';
import { PUBLIC_URL } from '../utils/const';
import { BlogLanguageWrapper, BlogPage } from 'reactyll';
import MarkDown from 'react-markdown'
import rehypeRaw from 'rehype-raw';
import { Head } from 'vite-react-ssg';
import { useFathom } from '../contexts/FathomContext';
import { Container, Title, Text, Image, Box, Paper, Anchor, Group, Button, Stack, Divider } from '@mantine/core';
import { redirectWithParameters } from '../utils/common';
import { getBlogByLanguage } from '../utils/blog';
import { blogs } from '../_blog/routes';
import { useTranslation } from 'react-i18next';
import MoreFromOurBlog from '../components/MoreFromOurBlog';

export interface ExtraProperties {
    date: string
    headline: string
    description: string
    publishDate: string
    readMore?: string[]
}

const content: BlogPage<ExtraProperties> = JSON.parse(decodeURIComponent(atob(`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`)));


const InnerBlogTemplate: React.FC = () => {
    const { trackEvent } = useFathom();
    const { i18n } = useTranslation();

    const matchingLanguageCode = content.properties.language || i18n.resolvedLanguage;    
    
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const langParam = urlParams.get('lang');

        if (langParam) {
            const matchingLanguage = content.languages.find(([lang, url]) => lang === langParam);
            if (matchingLanguage) {
                const [_, url] = matchingLanguage;
                window.location.href = `${url}`;
            }
        }
        

        const handleButtonClick = (event: any) => {
            if (event.target.classList.contains('cta')) {
                console.log("CTA clicked")
                trackEvent("Blog > CTA Clicked");
                window.location.href = "/login?signup=true"
            }
        };

        const parentElement = document.getElementById('blog-wrapper');
        if (parentElement) {
            document.addEventListener('click', handleButtonClick);
        }

        return () => {
            if (parentElement) {
                parentElement.removeEventListener('click', handleButtonClick);
            }
        };
    }, []);

    return (
        <Box bg="gray.0">
            <Head>
                <meta charSet="utf-8" />
                <title>{content.properties.title}</title>
                <link rel="canonical" href={`https://calensync.live${content.properties.url}`} />
                {content.languages.map(([language, url]) => (
                    <link rel="alternate" key={language} href={`https://calensync.live${url}`} hrefLang={language} />
                ))}
                <meta name="description" content={content.properties.description} />
                <meta property="og:title" content={content.properties.title} />
                <meta property="og:url" content={`https://calensync.live${content.properties.url}`} />
                <meta property="og:description" content={content.properties.description} />
                <meta property="og:publish_date" content={content.properties.publishDate} />
            </Head>
            <Container size="md" mt="md" id="blog-wrapper">
                <Paper p="md" my="md" bg="gray.0">
                    <Box mb="md" mt="md">
                        <Title order={1} mb="xs">{content.properties.title}</Title>
                        <Text fs="italic" c="dimmed">{content.properties.date}</Text>
                    </Box>

                    <Box mb="xl">
                        <MarkDown
                            rehypePlugins={[rehypeRaw]}
                            children={content.markdown}
                            components={{
                                h1(props) {
                                    const { children } = props
                                    return <Title order={1}>{children}</Title>
                                },
                                p(props) {
                                    const { children } = props
                                    return <Text size="lg" mb="md">
                                        {children}
                                    </Text>
                                },
                                strong(props) {
                                    const { children } = props
                                    return <Text span fw={700}><b>{children}</b></Text>
                                },
                                h2(props) {
                                    const { children } = props
                                    return <Title fw={400} order={2} mt="xl" pt="md" pb="md">{children}</Title>
                                },
                                h3(props) {
                                    const { children } = props
                                    return <Title fz="xl" fw={300} order={3} mt="md" pt="sm" pb="sm">{children}</Title>
                                },
                                img(props) {
                                    const { src, alt, style, width } = props
                                    return <Image
                                        mx="auto"
                                        my="sm"
                                        src={src}
                                        alt={alt}
                                        style={style}
                                        w={width}
                                        radius="md"
                                    />
                                },
                                a(props) {
                                    const { children, href } = props
                                    if (typeof children === 'string' && children.includes('CUSTOM_BUTTON')) {
                                        const content = children.replace('CUSTOM_BUTTON:', '')
                                        const onClick = () => {
                                            console.log("CTA clicked")
                                            trackEvent("Blog > CTA Clicked");
                                            redirectWithParameters('login', { signup: 'true' }, true)
                                        }
                                        return (
                                            <Group justify='center' my='md'>
                                                <Stack justify='center' my='md' gap="0">
                                                    <Button color="primary.4" size="compact-md" className="cta" onClick={onClick}>{content}</Button>
                                                    <Text size="sm" c="dimmed">.. and get 7 days free trial</Text>
                                                </Stack>
                                            </Group>
                                        )
                                    } else if (href.toLowerCase().startsWith('blog/')) {
                                        const blogId = href.toLowerCase().replace('blog/', '')
                                        const content = getBlogByLanguage(blogs[blogId], matchingLanguageCode)
                                        return <Anchor href={content.url}>{children}</Anchor>
                                    } else {
                                        return <Anchor href={href}>{children}</Anchor>
                                    }
                                }
                            }}
                        />
                    </Box>
                    {content.properties.readMore && (
                        <MoreFromOurBlog 
                            blogs={content.properties.readMore.map((blogId) => blogs[blogId])} 
                            matchingLanguageCode={matchingLanguageCode} 
                        />
                    )}
                </Paper>
            </Container>
        </Box>
    );
};

const BlogTemplate: React.FC = () => {
    return (
        <Layout>
            <InnerBlogTemplate />
        </Layout>
    );
};

export default BlogTemplate;